import React, { useEffect } from 'react';
import Layout from '../layouts/Layout';
import { PUBLIC_URL } from '../utils/const';
import { BlogLanguageWrapper, BlogPage } from 'reactyll';
import MarkDown from 'react-markdown'
import rehypeRaw from 'rehype-raw';
import { Head } from 'vite-react-ssg';
import { useFathom } from '../contexts/FathomContext';
import { Container, Title, Text, Image, Box, Paper, Anchor, Group, Button, Stack, Divider } from '@mantine/core';
import { redirectWithParameters } from '../utils/common';
import { getBlogByLanguage } from '../utils/blog';
import { blogs } from '../_blog/routes';
import { useTranslation } from 'react-i18next';
import MoreFromOurBlog from '../components/MoreFromOurBlog';

export interface ExtraProperties {
    date: string
    headline: string
    description: string
    publishDate: string
    readMore?: string[]
}

const content: BlogPage<ExtraProperties> = JSON.parse(decodeURIComponent(atob(`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`)));


const InnerBlogTemplate: React.FC = () => {
    const { trackEvent } = useFathom();
    const { i18n } = useTranslation();

    const matchingLanguageCode = content.properties.language || i18n.resolvedLanguage;    
    
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const langParam = urlParams.get('lang');

        if (langParam) {
            const matchingLanguage = content.languages.find(([lang, url]) => lang === langParam);
            if (matchingLanguage) {
                const [_, url] = matchingLanguage;
                window.location.href = `${url}`;
            }
        }
        

        const handleButtonClick = (event: any) => {
            if (event.target.classList.contains('cta')) {
                console.log("CTA clicked")
                trackEvent("Blog > CTA Clicked");
                window.location.href = "/login?signup=true"
            }
        };

        const parentElement = document.getElementById('blog-wrapper');
        if (parentElement) {
            document.addEventListener('click', handleButtonClick);
        }

        return () => {
            if (parentElement) {
                parentElement.removeEventListener('click', handleButtonClick);
            }
        };
    }, []);

    return (
        <Box bg="gray.0">
            <Head>
                <meta charSet="utf-8" />
                <title>{content.properties.title}</title>
                <link rel="canonical" href={`https://calensync.live${content.properties.url}`} />
                {content.languages.map(([language, url]) => (
                    <link rel="alternate" key={language} href={`https://calensync.live${url}`} hrefLang={language} />
                ))}
                <meta name="description" content={content.properties.description} />
                <meta property="og:title" content={content.properties.title} />
                <meta property="og:url" content={`https://calensync.live${content.properties.url}`} />
                <meta property="og:description" content={content.properties.description} />
                <meta property="og:publish_date" content={content.properties.publishDate} />
            </Head>
            <Container size="md" mt="md" id="blog-wrapper">
                <Paper p="md" my="md" bg="gray.0">
                    <Box mb="md" mt="md">
                        <Title order={1} mb="xs">{content.properties.title}</Title>
                        <Text fs="italic" c="dimmed">{content.properties.date}</Text>
                    </Box>

                    <Box mb="xl">
                        <MarkDown
                            rehypePlugins={[rehypeRaw]}
                            children={content.markdown}
                            components={{
                                h1(props) {
                                    const { children } = props
                                    return <Title order={1}>{children}</Title>
                                },
                                p(props) {
                                    const { children } = props
                                    return <Text size="lg" mb="md">
                                        {children}
                                    </Text>
                                },
                                strong(props) {
                                    const { children } = props
                                    return <Text span fw={700}><b>{children}</b></Text>
                                },
                                h2(props) {
                                    const { children } = props
                                    return <Title fw={400} order={2} mt="xl" pt="md" pb="md">{children}</Title>
                                },
                                h3(props) {
                                    const { children } = props
                                    return <Title fz="xl" fw={300} order={3} mt="md" pt="sm" pb="sm">{children}</Title>
                                },
                                img(props) {
                                    const { src, alt, style, width } = props
                                    return <Image
                                        mx="auto"
                                        my="sm"
                                        src={src}
                                        alt={alt}
                                        style={style}
                                        w={width}
                                        radius="md"
                                    />
                                },
                                a(props) {
                                    const { children, href } = props
                                    if (typeof children === 'string' && children.includes('CUSTOM_BUTTON')) {
                                        const content = children.replace('CUSTOM_BUTTON:', '')
                                        const onClick = () => {
                                            console.log("CTA clicked")
                                            trackEvent("Blog > CTA Clicked");
                                            redirectWithParameters('login', { signup: 'true' }, true)
                                        }
                                        return (
                                            <Group justify='center' my='md'>
                                                <Stack justify='center' my='md' gap="0">
                                                    <Button color="primary.4" size="compact-md" className="cta" onClick={onClick}>{content}</Button>
                                                    <Text size="sm" c="dimmed">.. and get 7 days free trial</Text>
                                                </Stack>
                                            </Group>
                                        )
                                    } else if (href.toLowerCase().startsWith('blog/')) {
                                        const blogId = href.toLowerCase().replace('blog/', '')
                                        const content = getBlogByLanguage(blogs[blogId], matchingLanguageCode)
                                        return <Anchor href={content.url}>{children}</Anchor>
                                    } else {
                                        return <Anchor href={href}>{children}</Anchor>
                                    }
                                }
                            }}
                        />
                    </Box>
                    {content.properties.readMore && (
                        <MoreFromOurBlog 
                            blogs={content.properties.readMore.map((blogId) => blogs[blogId])} 
                            matchingLanguageCode={matchingLanguageCode} 
                        />
                    )}
                </Paper>
            </Container>
        </Box>
    );
};

const BlogTemplate: React.FC = () => {
    return (
        <Layout>
            <InnerBlogTemplate />
        </Layout>
    );
};

export default BlogTemplate;